import { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Card, Button, Carousel, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiArrowBack, BiChevronLeft, BiChevronRight, BiFilterAlt } from 'react-icons/bi';
import { LoaderContext, RegulationContext, SidebarContext } from '../../../App';
import Nodataimg from '../../../images/Assessment.png';
import { CompareCategoryType, DataViewCategoryType, LS_REGULATION_SELECTED_KEY, LS_COMPANY_INFO_KEY } from '../../../core/constants/common';
import CompanyDashboardService from '../../../core/service/CompanyDashboardService';
import ChartMenu from '../../../components/ChartMenu';
import '../../Common/Dataview/DataView.scss';
import './ESGAnalytics.scss';
import ESGAnalyticsCards from './ESGAnalyticsCards';
import ESGUnitWiseConfigForm from './ESGUnitWiseConfigForm';
import ESGAnalyticsLineGraph from './ESGAnalyticsLineGraph';
import ESGAnalyticsLineGraphYearly from './ESGAnalyticsLineGraphYearly';
import ESGAnalyticsStackedBarChart from './ESGAnalyticsStackedBarChart';
import ESGAnalyticsConfigPanel from './ESGAnalyticsConfigPanel';
import ESGAnalyticsSummaryCard from './ESGAnalyticsSummaryCard';
import {
  collectStackedBarCarouselItemsFromDataView,
  getConfigRowForStackedBarCarouselItem,
} from '../../../core/utilities/esgAnalyticsStackedBarCarousel';
import { getAnalyticsFiltersTotalCount } from '../../../core/utilities/esgAnalyticsFilters';

/** Reporting period year list bounds (2022–2027); used by ESGUnitWiseConfigForm sidebar. */
export {
  ESG_ANALYTICS_REPORTING_PERIOD_END_YEAR,
  ESG_ANALYTICS_REPORTING_PERIOD_START_YEAR,
} from './esgAnalyticsPeriodRange';

// Yearly period pill: same FY/CY + financialYearStart/End rules as HomePage (local copy — HomePage keeps its own).
const COMPARE_MONTH_LABEL_KEYS = {
  JANUARY: 'January.label', FEBRUARY: 'February.label', MARCH: 'March.label', APRIL: 'April.label',
  MAY: 'May.label', JUNE: 'June.label', JULY: 'July.label', AUGUST: 'August.label',
  SEPTEMBER: 'September.label', OCTOBER: 'October.label', NOVEMBER: 'November.label', DECEMBER: 'December.label',
};
const COMPARE_MONTH_NUMS = {
  JANUARY: 1, FEBRUARY: 2, MARCH: 3, APRIL: 4, MAY: 5, JUNE: 6, JULY: 7, AUGUST: 8,
  SEPTEMBER: 9, OCTOBER: 10, NOVEMBER: 11, DECEMBER: 12,
};

export function getCompareYearPeriodRangeLabel(t, yearNum, companyInfo = {}) {
  const y = Number(yearNum);
  const safeY = Number.isFinite(y) ? y : new Date().getFullYear();
  const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
  if (dataFreqType === 'FY') {
    const startMonth = companyInfo?.financialYearStart || 'APRIL';
    const endMonth = companyInfo?.financialYearEnd || 'MARCH';
    const startLabel = COMPARE_MONTH_LABEL_KEYS[startMonth] ? t(COMPARE_MONTH_LABEL_KEYS[startMonth]) : t('April.label');
    const endLabel = COMPARE_MONTH_LABEL_KEYS[endMonth] ? t(COMPARE_MONTH_LABEL_KEYS[endMonth]) : t('March.label');
    const sm = COMPARE_MONTH_NUMS[startMonth] ?? 4;
    const em = COMPARE_MONTH_NUMS[endMonth] ?? 3;
    const startY = sm > em ? safeY - 1 : safeY;
    return `${startLabel} ${startY} ${t('HomePage.periodTo.label')} ${endLabel} ${safeY}`;
  }
  return `${t('January.label')} ${safeY} ${t('HomePage.periodTo.label')} ${t('December.label')} ${safeY}`;
}

/** All distinct fiscal/calendar years selected in YEARLY mode, sorted ascending (for compare header pill). */
export function getSortedYearlyYearsFromSelectedRepPeriod(selectedRepPeriod, fallbackYear = new Date().getFullYear()) {
  if (!selectedRepPeriod || !Array.isArray(selectedRepPeriod)) {
    return [fallbackYear];
  }
  const years = selectedRepPeriod
    .map((p) => p?.value)
    .filter((v) => v != null && v !== '')
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n));
  const uniqueSorted = [...new Set(years)].sort((a, b) => a - b);
  return uniqueSorted.length > 0 ? uniqueSorted : [fallbackYear];
}

const ESGAnalyticsView = ({ t }) => {
  const { glbRegulation } = useContext(RegulationContext);
  const { setLoaderDisplay } = useContext(LoaderContext);
  const { setSidebarDisplay } = useContext(SidebarContext);
  const glbRegulationId = glbRegulation?.id ?? localStorage.getItem(LS_REGULATION_SELECTED_KEY) ?? "";

  const [filter, setFilter] = useState({
    standardId: glbRegulationId,
    periodType: 'YEARLY',
    period: null,
    category: 'ENVIRONMENTAL',
    categoryType: DataViewCategoryType.ENVIRONMENTAL,
    topicIds: [],
    metricIds: [],
    datapointIds: [],
    parentBranchIds: [],
    childBranchIds: [],
    businessUnitIds: [],
    childEntities: [],
    parentEntities: [],
    selectedRepPeriod: [],
    nullInfoConfig: [],
  });

  // Filter that was used when data was last fetched (Apply clicked). Cards use this so they only update on Apply.
  const [appliedFilter, setAppliedFilter] = useState(null);

  const [dataViewData, setDataViewData] = useState([]);
  const [graphData, setGraphData] = useState(null);
  const [dataPoints, setDataPoints] = useState({});
  const [yearData, setYearData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [configRows, setConfigRows] = useState([]);
  const [filterId, setFilterId] = useState(null);
  const [title, setTitle] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [configList, setConfigList] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [hasSummaryData, setHasSummaryData] = useState(true);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryItemsPerPage, setSummaryItemsPerPage] = useState(10);
  const [summaryTotalCount, setSummaryTotalCount] = useState(0);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [showHeader, setShowHeader] = useState(false);

  const getFrequencyLabel = (periodType) => {
    const pt = periodType || 'YEARLY';
    if (pt === 'YEARLY') return t("Yearly.label");
    if (pt === 'QUARTERLY') return t("Quarterly.label");
    if (pt === 'MONTHLY') return t("Monthly.label");
    return t("Yearly.label");
  };

  const getReportingPeriodLabel = (f, maxDisplay) => {
    const selected = f?.selectedRepPeriod;
    if (!selected || !Array.isArray(selected) || selected.length === 0) {
      return t("ESGAnalytics.notSelected.label") || '—';
    }
    const periodType = f?.periodType || 'YEARLY';
    const parts = selected.map((period) => {
      if (periodType === 'YEARLY') {
        return period.value ? String(period.value) : '';
      }
      if (periodType === 'QUARTERLY') {
        const year = period.category ? String(period.category) : '';
        const quarter = period.value || '';
        return year && quarter ? `${year}-${quarter}` : '';
      }
      if (periodType === 'MONTHLY') {
        const year = period.category ? String(period.category) : '';
        const month = period.value || '';
        return year && month ? `${year}-${month}` : '';
      }
      return '';
    }).filter(Boolean);
    if (parts.length === 0) return t("ESGAnalytics.notSelected.label") || '—';
    if (maxDisplay != null && parts.length > maxDisplay) {
      const shown = parts.slice(0, maxDisplay).join(', ');
      return `${shown} ...`;
    }
    return parts.join(', ');
  };

  const getOrganisationHierarchyLabel = (branchList, maxDisplay) => {
    if (!branchList || !Array.isArray(branchList) || branchList.length === 0) {
      return t("ESGAnalytics.notSelected.label") || '—';
    }
    const names = branchList.map(b => b.branchName || '').filter(Boolean);
    if (names.length === 0) return t("ESGAnalytics.notSelected.label") || '—';
    if (maxDisplay != null && names.length > maxDisplay) {
      const shown = names.slice(0, maxDisplay).join(', ');
      return `${shown} ...`;
    }
    return names.join(', ');
  };

  let sortOrder = [];

  const fetchAnalyticsFilters = useCallback((pageNumber = 1, pageSize = 10) => {
    if (!glbRegulationId) return;

    const apiPageNumber = pageNumber > 0 ? pageNumber - 1 : 0;

    setLoaderDisplay(true);
    CompanyDashboardService.getAnalyticsFilters(glbRegulationId, 'CONFIGURATION', apiPageNumber, pageSize)
      .then(res => {
        setLoaderDisplay(false);
        setIsDataLoaded(true);
        if (res.status === 200 && res.data) {
          const data = Array.isArray(res.data) ? res.data : (res.data.data || res.data.content || []);
          const totalCount = getAnalyticsFiltersTotalCount(res, data.length);
          setSummaryTotalCount(totalCount);

          if (data.length === 0 && pageNumber > 1 && totalCount > 0) {
            setSummaryPage(pageNumber - 1);
            return;
          }

          if (data && Array.isArray(data) && data.length > 0) {
            setSummaryData(data);
            setHasSummaryData(true);
          } else {
            setSummaryData(null);
            setHasSummaryData(false);
          }
        } else {
          setSummaryTotalCount(0);
          setSummaryData(null);
          setHasSummaryData(false);
        }
      })
      .catch(() => {
        setLoaderDisplay(false);
        setIsDataLoaded(true);
        setSummaryTotalCount(0);
        setSummaryData(null);
        setHasSummaryData(false);
      });
  }, [glbRegulationId, setLoaderDisplay]);

  const handleSummaryPageChange = (pageNum, perPage) => {
    if (perPage) {
      setSummaryItemsPerPage(perPage);
      setSummaryPage(1);
      return;
    }
    if (pageNum > 0) {
      setSummaryPage(pageNum);
    }
  };

  // Fetch analytics filters/summary data on component mount and when pagination changes
  useEffect(() => {
    setSummaryPage(1);
  }, [glbRegulationId]);

  useEffect(() => {
    fetchAnalyticsFilters(summaryPage, summaryItemsPerPage);
  }, [fetchAnalyticsFilters, summaryPage, summaryItemsPerPage]);

  // Collapse main sidebar when in view mode (viewing a configuration), restore when showing summary or empty state
  useEffect(() => {
    const isViewingConfiguration = configRows.length > 0 || filterId !== null;
    if (!hasSummaryData && isViewingConfiguration) {
      // View mode (viewing a configuration): collapse main sidebar
      setSidebarDisplay(false);
    } else {
      // Summary view or empty state: restore main sidebar
      setSidebarDisplay(true);
    }
  }, [hasSummaryData, configRows.length, filterId, setSidebarDisplay]);

  // Cleanup: restore sidebar when component unmounts
  useEffect(() => {
    return () => {
      setSidebarDisplay(true);
    };
  }, [setSidebarDisplay]);

  // Handle scroll detection for sticky sidebar
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 0);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const loadFilterConfiguration = (item) => {
    if (!item || !item.configData) return;

    try {
      const configData = JSON.parse(item.configData);
      if (!configData) return;

      // Create a map of equation to verticalCalcEnabled from saved equationConfig
      const equationConfigMap = new Map();
      if (configData.equationConfig && Array.isArray(configData.equationConfig)) {
        configData.equationConfig.forEach(eqConfig => {
          if (eqConfig.equation) {
            equationConfigMap.set(eqConfig.equation, eqConfig.verticalCalcEnabled || false);
          }
        });
      }

      // Transform saved configRows to match the new UI structure
      const mappedConfigRows = (configData.configRows || []).map((configRow) => {
        // Transform level1 - can be either metric or datapoint
        let level1Metric = null;
        let level1Datapoint = null;
        let useLevel1Equation = false;
        let level1Equation = null;
        let level1VerticalCalcEnabled = configRow.level1VerticalCalcEnabled || false;

        if (configRow.level1) {
          // Check if level1 has an equation property
          if (configRow.level1.itemType === 'equation' && configRow.level1.eqExpressionWithId) {
            const level1EquationId = configRow.level1.eqExpressionWithId;
            useLevel1Equation = true;
            level1Equation = {
              expressionWithId: level1EquationId,
              expression: configRow.level1.eqExpression
            };
            // Get verticalCalcEnabled from equationConfig map if available
            if (equationConfigMap.has(level1EquationId)) {
              level1VerticalCalcEnabled = equationConfigMap.get(level1EquationId);
            }
            const { eqExpressionWithId, eqExpression, ...cleanLevel1 } = configRow.level1;
            if (cleanLevel1.itemType === 'metric') {
              level1Metric = cleanLevel1;
            } else if (cleanLevel1.itemType === 'datapoint') {
              level1Datapoint = cleanLevel1;
            } else {
              level1Metric = cleanLevel1;
            }
          } else {
            if (configRow.level1.itemType === 'metric') {
              level1Metric = configRow.level1;
            } else if (configRow.level1.itemType === 'datapoint') {
              level1Datapoint = configRow.level1;
            } else {
              level1Metric = configRow.level1;
            }
          }
        }

        // Transform level2
        let level2Rows = [];
        const level2Metrics = [];
        const level2Datapoints = [];

        const level2ItemMap = new Map();
        if (configRow.level2 && Array.isArray(configRow.level2)) {
          configRow.level2.forEach(item => {
            const key = item.label || `${item.itemType}_${item.id}`;
            level2ItemMap.set(key, item);
          });
        }

        if (configRow.level2Rows && Array.isArray(configRow.level2Rows)) {
          level2Rows = configRow.level2Rows.map(row => {
            const rowData = {
              id: row.id || Date.now() + Math.random(),
              label: row.label || '',
              metric: row.metric || null,
              datapoint: row.datapoint || null,
              useEquation: false,
              equation: null,
              verticalCalcEnabled: row.verticalCalcEnabled || false,
              drillDownEnabled: row.drillDownEnabled || false,
              drillDownType: row.drillDownType || 'equation',
              metricSearchTerm: '',
              datapointSearchTerm: '',
              showMetricDropdown: false,
              showDatapointDropdown: false
            };
            const key = row.label || `${row.metric ? 'metric' : 'datapoint'}_${row.metric?.id || row.datapoint?.id}`;
            const level2Item = level2ItemMap.get(key);
            if (level2Item && level2Item.eqExpressionWithId) {
              const level2EquationId = level2Item.eqExpressionWithId;
              rowData.useEquation = true;
              rowData.equation = {
                expressionWithId: level2EquationId,
                expression: level2Item.eqExpression
              };
              // Get verticalCalcEnabled from equationConfig map if available
              if (equationConfigMap.has(level2EquationId)) {
                rowData.verticalCalcEnabled = equationConfigMap.get(level2EquationId);
              }
            }
            return rowData;
          });
          level2Rows.forEach(row => {
            if (row.metric) level2Metrics.push(row.metric);
            if (row.datapoint) level2Datapoints.push(row.datapoint);
          });
        } else if (configRow.level2 && Array.isArray(configRow.level2)) {
          configRow.level2.forEach(item => {
            const row = {
              id: Date.now() + Math.random(),
              label: item.label || '',
              metric: null,
              datapoint: null,
              useEquation: false,
              equation: null,
              verticalCalcEnabled: item.verticalCalcEnabled || false,
              drillDownEnabled: item.drillDownEnabled || false,
              drillDownType: item.drillDownType || 'equation',
              metricSearchTerm: '',
              datapointSearchTerm: '',
              showMetricDropdown: false,
              showDatapointDropdown: false
            };
            if (item.eqExpressionWithId) {
              const level2EquationId = item.eqExpressionWithId;
              row.useEquation = true;
              row.equation = {
                expressionWithId: level2EquationId,
                expression: item.eqExpression
              };
              // Get verticalCalcEnabled from equationConfig map if available
              if (equationConfigMap.has(level2EquationId)) {
                row.verticalCalcEnabled = equationConfigMap.get(level2EquationId);
              }
              const { eqExpressionWithId, eqExpression, ...cleanItem } = item;
              if (cleanItem.itemType === 'metric') {
                row.metric = cleanItem;
                level2Metrics.push(cleanItem);
              } else if (cleanItem.itemType === 'datapoint') {
                row.datapoint = cleanItem;
                level2Datapoints.push(cleanItem);
              }
            } else {
              if (item.itemType === 'metric') {
                row.metric = item;
                level2Metrics.push(item);
              } else if (item.itemType === 'datapoint') {
                row.datapoint = item;
                level2Datapoints.push(item);
              }
            }
            level2Rows.push(row);
          });
        }

        const sortOrderRaw = Number(configRow.sortOrder);
        const sortOrderSaved = Number.isFinite(sortOrderRaw) ? sortOrderRaw : 0;
        return {
          id: configRow.id || Date.now() + Math.random(),
          name: configRow.name || '',
          level1Metric,
          level1Datapoint,
          level2Rows,
          level2Metrics,
          level2Datapoints,
          level1: configRow.level1,
          level2: configRow.level2,
          useLevel1Equation,
          level1Equation,
          level1VerticalCalcEnabled,
          level1MetricSearchTerm: level1Metric ? (level1Metric.name || level1Metric.code) : '',
          level1DatapointSearchTerm: level1Datapoint ? (level1Datapoint.name || level1Datapoint.code) : '',
          showLevel1MetricDropdown: false,
          showLevel1DatapointDropdown: false,
          showPercentage: configRow.showPercentage !== false,
          sortOrder: sortOrderSaved,
        };
      });

      const transformedConfigRows = [...mappedConfigRows].sort((a, b) => {
        return Number(a.sortOrder) - Number(b.sortOrder);
      }).map((row, i) => ({
        ...row,
        sortOrder: i + 1,
      }));

      // Populate filter state from saved configuration
      const parentEntities = configData.parentEntities || configData.parentBranchIds || [];
      const childEntities = configData.childEntities || configData.childBranchIds || [];

      let categoryType = configData.categoryType;
      if (!categoryType && configData.category) {
        const categoryMap = {
          'ENVIRONMENTAL': DataViewCategoryType.ENVIRONMENTAL,
          'SOCIAL': DataViewCategoryType.SOCIAL,
          'GOVERNANCE': DataViewCategoryType.GOVERNANCE
        };
        categoryType = categoryMap[configData.category] || DataViewCategoryType.ENVIRONMENTAL;
      }
      categoryType = categoryType || DataViewCategoryType.ENVIRONMENTAL;

      let selectedRepPeriod = configData.selectedRepPeriod || [];
      if (!selectedRepPeriod || selectedRepPeriod.length === 0) {
        const periodObj = configData.period;
        const periodType = configData.periodType || 'YEARLY';

        if (periodObj && typeof periodObj === 'object') {
          selectedRepPeriod = [];

          if (periodType === 'YEARLY') {
            Object.keys(periodObj).forEach(year => {
              selectedRepPeriod.push({
                value: parseInt(year),
                category: 'Years'
              });
            });
          } else if (periodType === 'QUARTERLY') {
            Object.keys(periodObj).forEach(year => {
              const quarters = periodObj[year];
              if (Array.isArray(quarters)) {
                quarters.forEach(quarter => {
                  selectedRepPeriod.push({
                    value: quarter,
                    category: parseInt(year)
                  });
                });
              }
            });
          } else if (periodType === 'MONTHLY') {
            Object.keys(periodObj).forEach(year => {
              const months = periodObj[year];
              if (Array.isArray(months)) {
                months.forEach(month => {
                  selectedRepPeriod.push({
                    value: month,
                    category: parseInt(year)
                  });
                });
              }
            });
          }
        }
      }

      const savedFilter = {
        standardId: configData.standardId || glbRegulationId,
        periodType: configData.periodType || 'YEARLY',
        period: configData.period || null,
        category: configData.category || 'ENVIRONMENTAL',
        categoryType: categoryType,
        topicIds: configData.topicIds || [],
        metricIds: configData.metricIds || [],
        datapointIds: configData.datapointIds || [],
        parentBranchIds: parentEntities,
        childBranchIds: childEntities,
        businessUnitIds: [...parentEntities, ...childEntities],
        childEntities: childEntities,
        parentEntities: parentEntities,
        selectedRepPeriod: selectedRepPeriod,
      };

      setFilter(savedFilter);
      setConfigRows(transformedConfigRows);
      setTitle(configData.filterName || configData.title || '');
      setFilterId(item.id);

      // Apply filters to load data
      if (transformedConfigRows.length > 0 && savedFilter.period) {
        const { metricIds, datapointIds } = extractMetricAndDatapointIds(transformedConfigRows);
        // Use saved equationConfig if available, otherwise extract from configRows
        let equationConfig = configData.equationConfig || [];
        let equations = configData.equations || [];

        // If equationConfig is not available, extract from configRows
        if (!equationConfig || equationConfig.length === 0) {
          const extracted = extractEquations(transformedConfigRows);
          equations = extracted.equations;
          equationConfig = extracted.equationConfig;
        }

        const updatedFilter = {
          ...savedFilter,
          metricIds,
          datapointIds,
          equations,
          equationConfig,
          nullInfoConfig: buildNullInfoConfig(transformedConfigRows)
        };
        applyFilter(updatedFilter);
        getGraphData(updatedFilter, metricIds, datapointIds, equations, equationConfig);
      }
    } catch (error) {
      console.error('Error loading filter configuration:', error);
    }
  };

  const getDataViewData = (f) => {
    setLoaderDisplay(true);
    CompanyDashboardService.getMetricDVAnalytics(f)
      .then(res => {
        setLoaderDisplay(false);
        if (res.status !== 200) return;
        setDataViewData(res.data);
        processFilteredData(res.data);
      }).catch(err => {
        setLoaderDisplay(false);
      });
  };

  const getGraphData = (f, metricIds, datapointIds, equations = [], equationConfig = []) => {
    const periodType = f.periodType || 'YEARLY';
    const allMonths = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

    const quarterToMonths = {
      'Q1': ['JAN', 'FEB', 'MAR'],
      'Q2': ['APR', 'MAY', 'JUN'],
      'Q3': ['JUL', 'AUG', 'SEP'],
      'Q4': ['OCT', 'NOV', 'DEC']
    };

    const graphPeriod = {};

    if (f.selectedRepPeriod && Array.isArray(f.selectedRepPeriod) && f.selectedRepPeriod.length > 0) {
      if (periodType === 'YEARLY') {
        f.selectedRepPeriod.forEach(period => {
          const year = period.value ? String(period.value) : null;
          if (year && !graphPeriod[year]) {
            graphPeriod[year] = [...allMonths];
          }
        });
      } else if (periodType === 'QUARTERLY') {
        f.selectedRepPeriod.forEach(period => {
          const year = period.category ? String(period.category) : null;
          const quarter = period.value;

          if (year && quarter && quarterToMonths[quarter]) {
            if (!graphPeriod[year]) {
              graphPeriod[year] = [];
            }
            quarterToMonths[quarter].forEach(month => {
              if (!graphPeriod[year].includes(month)) {
                graphPeriod[year].push(month);
              }
            });
          }
        });
      } else if (periodType === 'MONTHLY') {
        f.selectedRepPeriod.forEach(period => {
          const year = period.category ? String(period.category) : null;
          const month = period.value;

          if (year && month && allMonths.includes(month)) {
            if (!graphPeriod[year]) {
              graphPeriod[year] = [];
            }
            if (!graphPeriod[year].includes(month)) {
              graphPeriod[year].push(month);
            }
          }
        });
      }
    } else if (f.period && typeof f.period === 'object') {
      if (periodType === 'YEARLY') {
        Object.keys(f.period).forEach(year => {
          graphPeriod[year] = [...allMonths];
        });
      } else if (periodType === 'QUARTERLY') {
        Object.keys(f.period).forEach(year => {
          const quarters = f.period[year];
          if (Array.isArray(quarters)) {
            graphPeriod[year] = [];
            quarters.forEach(quarter => {
              if (quarterToMonths[quarter]) {
                quarterToMonths[quarter].forEach(month => {
                  if (!graphPeriod[year].includes(month)) {
                    graphPeriod[year].push(month);
                  }
                });
              }
            });
          }
        });
      } else if (periodType === 'MONTHLY') {
        Object.keys(f.period).forEach(year => {
          const months = f.period[year];
          if (Array.isArray(months)) {
            graphPeriod[year] = months.filter(month => allMonths.includes(month));
          }
        });
      }
    }

    if (Object.keys(graphPeriod).length === 0) {
      setGraphData(null);
      return;
    }

    const graphFilter = {
      standardId: f.standardId || glbRegulationId,
      periodType: 'MONTHLY',
      period: graphPeriod,
      category: f.categoryType ? CompareCategoryType[f.categoryType] : 'ENVIRONMENTAL',
      topicIds: f.topicIds || [],
      metricIds: metricIds || [],
      datapointIds: datapointIds || [],
      equations: equations || [],
      equationConfig: equationConfig || []
    };

    // POST api/metric/dv/analytics/graph - disabled so API is not hit on load
    // setLoaderDisplay(true);
    // CompanyDashboardService.getMetricDVGraph(graphFilter)
    //   .then(res => {
    //     setLoaderDisplay(false);
    //     if (res.status !== 200) {
    //       setGraphData(null);
    //       return;
    //     }
    //     setGraphData(res.data.data || res.data);
    //   }).catch(err => {
    //     setLoaderDisplay(false);
    //     setGraphData(null);
    //   });
    setGraphData(null);
  };

  const applyFilter = (f) => {
    const compareFilter = {
      standardId: f.standardId || glbRegulationId,
      periodType: f.periodType,
      period: f.period,
      category: f.categoryType ? CompareCategoryType[f.categoryType] : 'ENVIRONMENTAL',
      topicIds: f.topicIds || [],
      metricIds: f.metricIds || [],
      datapointIds: f.datapointIds || [],
      equations: f.equations || [],
      equationConfig: f.equationConfig || [],
      parentBranchIds: f.parentEntities || [],
      childBranchIds: f.childEntities || [],
      businessUnitIds: [...(f.parentEntities || []), ...(f.childEntities || [])],
      childEntities: f.childEntities || [],
      parentEntities: f.parentEntities || [],
      selectedRepPeriod: f.selectedRepPeriod || [],
      nullInfoConfig: f.nullInfoConfig || [],
    };
    setFilter(compareFilter);
    setAppliedFilter(compareFilter);
    sortOrder = compareFilter.businessUnitIds;
    const p = {
      ...compareFilter,
    };
    getDataViewData(p);
  };

  const processFilteredData = (data) => {
    let yearData = [];
    const branchesSet = new Map();

    if (data) {
      for (const [parentKey, patentValue] of Object.entries(data)) {
        if (patentValue) {
          for (const [childKey, childValue] of Object.entries(patentValue)) {
            yearData.push(parentKey + (childKey != 'YEARLY' ? '-' + childKey : ''));

            if (childValue && typeof childValue === 'object' && !Array.isArray(childValue)) {
              const { metrics = [], datapoints = [], equations = [] } = childValue;

              metrics.forEach(metric => {
                if (metric.branchConsolidated && Array.isArray(metric.branchConsolidated)) {
                  metric.branchConsolidated.forEach(branch => {
                    if (branch.branchId && !branchesSet.has(branch.branchId)) {
                      branchesSet.set(branch.branchId, {
                        branchId: branch.branchId,
                        branchName: branch.branchName,
                        parentBranchId: branch.parentBranchId
                      });
                    }
                  });
                }
              });

              datapoints.forEach(datapoint => {
                if (datapoint.branchConsolidated && Array.isArray(datapoint.branchConsolidated)) {
                  datapoint.branchConsolidated.forEach(branch => {
                    if (branch.branchId && !branchesSet.has(branch.branchId)) {
                      branchesSet.set(branch.branchId, {
                        branchId: branch.branchId,
                        branchName: branch.branchName,
                        parentBranchId: branch.parentBranchId
                      });
                    }
                  });
                }
              });

              equations.forEach(equation => {
                if (equation.branchConsolidated && Array.isArray(equation.branchConsolidated)) {
                  equation.branchConsolidated.forEach(branch => {
                    if (branch.branchId && !branchesSet.has(branch.branchId)) {
                      branchesSet.set(branch.branchId, {
                        branchId: branch.branchId,
                        branchName: branch.branchName,
                        parentBranchId: branch.parentBranchId
                      });
                    }
                  });
                }
              });
            }
          }
        }
      }
      setYearData(yearData);
      setBranches(Array.from(branchesSet.values()));
    }
  };

  const handleFilterChange = (newFilters) => {
    const compareFilter = {
      standardId: newFilters.standardId || glbRegulationId,
      periodType: newFilters.periodType || 'YEARLY',
      period: newFilters.period,
      category: newFilters.categoryType ? CompareCategoryType[newFilters.categoryType] : 'ENVIRONMENTAL',
      categoryType: newFilters.categoryType || DataViewCategoryType.ENVIRONMENTAL,
      topicIds: newFilters.topicIds || [],
      metricIds: newFilters.metricIds || [],
      datapointIds: newFilters.datapointIds || [],
      parentBranchIds: newFilters.parentEntities || [],
      childBranchIds: newFilters.childEntities || [],
      businessUnitIds: [...(newFilters.parentEntities || []), ...(newFilters.childEntities || [])],
      childEntities: newFilters.childEntities || [],
      parentEntities: newFilters.parentEntities || [],
      selectedRepPeriod: newFilters.selectedRepPeriod || [],
    };
    setFilter(compareFilter);
  };

  const extractMetricAndDatapointIds = (configRows) => {
    const metricIds = [];
    const datapointIds = [];

    configRows.forEach(config => {
      if (config.level1 && config.level1.id) {
        if (config.level1.itemType === 'metric') {
          const metricId = config.level1.id;
          if (!metricIds.includes(metricId)) {
            metricIds.push(metricId);
          }
        } else if (config.level1.itemType === 'datapoint') {
          const datapointId = config.level1.id;
          if (!datapointIds.includes(datapointId)) {
            datapointIds.push(datapointId);
          }
        } else if (!config.level1.itemType) {
          const metricId = config.level1.id;
          if (!metricIds.includes(metricId)) {
            metricIds.push(metricId);
          }
        }
      }

      if (config.level2 && Array.isArray(config.level2)) {
        config.level2.forEach(item => {
          if (!item || !item.id) return;

          if (item.itemType === 'metric') {
            const metricId = item.id;
            if (!metricIds.includes(metricId)) {
              metricIds.push(metricId);
            }
          } else if (item.itemType === 'datapoint') {
            const datapointId = item.id;
            if (!datapointIds.includes(datapointId)) {
              datapointIds.push(datapointId);
            }
          }
        });
      }
    });

    return {
      metricIds: [...new Set(metricIds)],
      datapointIds: [...new Set(datapointIds)]
    };
  };

  const extractEquations = (configRows) => {
    const equations = [];
    const equationConfig = [];

    configRows.forEach(config => {
      const l1 = config.level1;
      if (l1?.itemType === 'equation' && l1.eqExpressionWithId) {
        const equation = l1.eqExpressionWithId;
        if (equation && !equations.includes(equation)) {
          equations.push(equation);
          equationConfig.push({
            equation: equation,
            verticalCalcEnabled: l1.verticalCalcEnabled || false
          });
        }
      }

      if (config.level2Rows && Array.isArray(config.level2Rows)) {
        config.level2Rows.forEach(row => {
          const includeLevel2Eq =
            row.useEquation && !row.metric && !row.datapoint && row.equation && row.equation.expressionWithId;
          if (includeLevel2Eq) {
            const equation = row.equation.expressionWithId;
            if (equation && !equations.includes(equation)) {
              equations.push(equation);
              equationConfig.push({
                equation: equation,
                verticalCalcEnabled: row.verticalCalcEnabled || false
              });
            }
          }
        });
      }
    });

    return { equations, equationConfig };
  };

  const handleApplyFilters = async (configRowsFromHeader) => {
    if (!filter.period || (filter.period && Object.keys(filter.period).length === 0)) {
      return;
    }
    const rowsToUse = configRowsFromHeader || configRows;
    if (!rowsToUse || rowsToUse.length === 0) {
      return;
    }
    const { metricIds, datapointIds } = extractMetricAndDatapointIds(rowsToUse);
    const { equations, equationConfig } = extractEquations(rowsToUse);
    const nullInfoConfig = buildNullInfoConfig(rowsToUse);
    const updatedFilter = {
      ...filter,
      metricIds: metricIds,
      datapointIds: datapointIds,
      equations: equations,
      equationConfig: equationConfig,
      nullInfoConfig: nullInfoConfig,
    };
    applyFilter(updatedFilter);
    getGraphData(updatedFilter, metricIds, datapointIds, equations, equationConfig);
  };

  const handleOpenConfigPanel = () => {
    setShowConfigPanel(true);
    fetchConfigList();
  };

  const handleCloseConfigPanel = () => {
    setShowConfigPanel(false);
  };

  const fetchConfigList = () => {
    if (!glbRegulationId) return;

    setLoadingConfigs(true);
    CompanyDashboardService.getAnalyticsFilters(glbRegulationId, 'CONFIGURATION', 0, 10)
      .then(res => {
        setLoadingConfigs(false);
        if (res.status === 200 && res.data) {
          const data = Array.isArray(res.data) ? res.data : (res.data.data || res.data.content || []);
          setConfigList(data || []);
        }
      })
      .catch(err => {
        setLoadingConfigs(false);
        setConfigList([]);
      });
  };

  const handleConfigSelect = (configItem) => {
    if (configItem && configItem.configData) {
      loadFilterConfiguration(configItem);
      setHasSummaryData(false);
      setShowConfigPanel(false);
    }
  };

  const handleBackToSummary = () => {
    // Refresh the list and show summary view
    fetchAnalyticsFilters(summaryPage, summaryItemsPerPage);
  };

  const handleCardClick = (item) => {
    // When card is clicked, load and view the configuration
    if (item && item.configData) {
      loadFilterConfiguration(item);
      setHasSummaryData(false);
    }
  };

  const handleViewClick = (item) => {
    // When view icon is clicked, load and view the configuration
    if (item && item.configData) {
      loadFilterConfiguration(item);
      setHasSummaryData(false);
    }
  };

  const handleEditClick = (item) => {
    // Navigate to edit mode (not used in view-only component, but kept for consistency)
    if (item && item.configData) {
      loadFilterConfiguration(item);
      setHasSummaryData(false);
    }
  };

  const handleNewClick = () => {
    // Not used in view-only component, but kept for consistency
    // In view mode, we don't allow creating new configurations
  };

  const handleDeleteSuccess = () => {
    // Refresh the list after deletion
    fetchAnalyticsFilters(summaryPage, summaryItemsPerPage);
  };

  const stackedBarChartCarouselItems = useMemo(
    () => collectStackedBarCarouselItemsFromDataView(dataViewData, configRows),
    [dataViewData, configRows]
  );

  const [chartsCarouselIndex, setChartsCarouselIndex] = useState(0);

  useEffect(() => {
    setChartsCarouselIndex(0);
  }, [stackedBarChartCarouselItems]);

  // Check if we're viewing a configuration (has configRows or filterId) vs showing empty state
  const isViewingConfiguration = configRows.length > 0 || filterId !== null;

  const activeFilterForPeriod = appliedFilter ?? filter;
  // Header display only: does not feed graphPeriod, save/load, or Apply — those use filter / appliedFilter elsewhere.
  const yearlyPeriodSelectedYears = useMemo(() => {
    if (activeFilterForPeriod?.periodType !== 'YEARLY') return null;
    return getSortedYearlyYearsFromSelectedRepPeriod(
      activeFilterForPeriod?.selectedRepPeriod,
      new Date().getFullYear()
    );
  }, [activeFilterForPeriod?.periodType, activeFilterForPeriod?.selectedRepPeriod]);

  /** Pill text: first year's full range label; append " ..." when more than one year is selected. */
  const compareYearlyPeriodPillText = useMemo(() => {
    if (yearlyPeriodSelectedYears == null || yearlyPeriodSelectedYears.length === 0) return '';
    let companyInfo = {};
    try {
      companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
    } catch {
      companyInfo = {};
    }
    const firstYear = yearlyPeriodSelectedYears[0];
    const firstRange = getCompareYearPeriodRangeLabel(t, firstYear, companyInfo);
    if (yearlyPeriodSelectedYears.length <= 1) return firstRange;
    return `${firstRange} ...`;
  }, [t, yearlyPeriodSelectedYears]);

  const compareYearlyPeriodTooltipLines = useMemo(() => {
    if (yearlyPeriodSelectedYears == null) return [];
    let companyInfo = {};
    try {
      companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
    } catch {
      companyInfo = {};
    }
    return yearlyPeriodSelectedYears.map((y) => getCompareYearPeriodRangeLabel(t, y, companyInfo));
  }, [t, yearlyPeriodSelectedYears]);

  const buildNullInfoConfig = (rows) => {
    const nullInfoConfig = [];
    const seen = new Set();

    (rows || []).forEach((config) => {
      const level1 = config?.level1;
      const level1Equation = level1?.eqExpressionWithId;
      if (level1?.itemType === 'metric' && level1.id > 0) {
        const key = `metric:${level1.id}:${level1Equation}`;
        if (!seen.has(key)) {
          seen.add(key);
          nullInfoConfig.push({
            id: level1.id,
            type: 'metric',
            equation: level1Equation
          });
        }
        return;
      }
      if (level1?.itemType === 'equation') {
        const key = `equation:${level1Equation}`;
        if (!seen.has(key)) {
          seen.add(key);
          nullInfoConfig.push({
            type: 'equation',
            equation: level1Equation
          });
        }
      }
    });

    return nullInfoConfig;
  };

  return (
    <div className={`ghg-emission-data-management ${!hasSummaryData ? 'esg-analytics-view-mode' : ''} ${showConfigPanel ? 'esg-analytics-config-panel-open' : ''}`}>
      {hasSummaryData ? (
        <ESGAnalyticsSummaryCard
          t={t}
          summaryData={summaryData}
          onNewClick={handleNewClick}
          onDeleteSuccess={handleDeleteSuccess}
          onCardClick={handleCardClick}
          onViewClick={handleViewClick}
          onEditClick={handleEditClick}
          hideActionIcons={true}
          title={t("Widgets.label") || "Widgets"}
          pagination={{
            itemsTotalCount: summaryTotalCount,
            itemsPerPage: summaryItemsPerPage,
            currentPage: summaryPage,
            setCurrentPage: setSummaryPage,
            setItemsPerPage: setSummaryItemsPerPage,
            onPageChange: handleSummaryPageChange,
          }}
        />
      ) : !isViewingConfiguration && isDataLoaded && (!summaryData || (Array.isArray(summaryData) && summaryData.length === 0)) ? (
        <div className="col-lg-12 px-0 py-2 text-center">
          <div className="noDataCard">
            <div>
              <div className="Nodataimg_box">
                <img src={Nodataimg} alt="No data" />
              </div>
              <h5 className="py-3">
                {t("noDataAvailable.label")}
              </h5>
            </div>
          </div>
        </div>
      ) : (
        <div className={`esg-analytics-view-layout ${isScrolled ? 'esg-analytics-scrolled' : ''}`}>
          <div className="List_header mb-2">
            <h5 className='MH'>{title}</h5>
            <div className='d-flex align-items-center gap-2 flex-wrap'>
              {/* Hidden for timebeing */}
              {/* <Button
                variant="primary"
                className="text-nowrap esg-analytics-select-config-btn me-2"
                onClick={handleOpenConfigPanel}
              >
                + {t("ESGAnalytics.selectConfiguration.label") || "Select Configuration"}
              </Button> */}
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="esg-analytics-frequency-tooltip">
                    {t("ESGAnalytics.selectedFrequency.label") || "Selected frequency"}: {getFrequencyLabel((appliedFilter ?? filter).periodType)}
                  </Tooltip>
                }
              >
                <span className="Filter_status">
                  {getFrequencyLabel((appliedFilter ?? filter).periodType)}
                </span>
              </OverlayTrigger>
              {activeFilterForPeriod?.periodType === 'YEARLY' && compareYearlyPeriodPillText ? (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id="esg-analytics-yearly-period-range-tooltip" className="esg-analytics-yearly-period-range-tooltip">
                      {compareYearlyPeriodTooltipLines.map((line, i) => (
                        <div key={i} className="esg-analytics-yearly-period-range-tooltip__line">
                          {line}
                        </div>
                      ))}
                    </Tooltip>
                  }
                >
                  <div
                    className="esg-analytics-period-range esg-analytics-period-range--has-tooltip"
                    tabIndex={0}
                    role="note"
                    aria-label={compareYearlyPeriodTooltipLines.join('. ')}
                  >
                    <span className="esg-analytics-period-range__label">{t('Period.label')}:</span>
                    <span className="esg-analytics-period-range__value">{compareYearlyPeriodPillText}</span>
                  </div>
                </OverlayTrigger>
              ) : (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id="esg-analytics-reporting-period-tooltip">
                      {t("ESGAnalytics.selectedReportingPeriod.label") || "Selected Reporting Period"}: {getReportingPeriodLabel(appliedFilter ?? filter)}
                    </Tooltip>
                  }
                >
                  <span className="Filter_status">
                    {getReportingPeriodLabel(appliedFilter ?? filter, 1)}
                  </span>
                </OverlayTrigger>
              )}
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="esg-analytics-org-hierarchy-tooltip">
                    {t("ESGAnalytics.orgHierarchy.label") || "Organisation Hierarchy"}: {getOrganisationHierarchyLabel(branches)}
                  </Tooltip>
                }
              >
                <span className="Filter_status">
                  {getOrganisationHierarchyLabel(branches, 2)}
                </span>
              </OverlayTrigger>
              <Button
                variant="outline-secondary"
                className="d-flex align-items-center gap-2 esg-analytics-back-button"
                onClick={() => setShowHeader(!showHeader)}
              >
                <BiFilterAlt size={18} />
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleBackToSummary}
                className="d-flex align-items-center gap-2 esg-analytics-back-button"
              >
                <BiArrowBack size={18} />
                {t("Back.label") || "Back"}
              </Button>
            </div>
          </div>
          <div className='esg-analytics-header-container 2'>
            {showHeader && (
              <ESGUnitWiseConfigForm
                t={t}
                onFilterChange={handleFilterChange}
                filter={filter}
                onApplyFilters={handleApplyFilters}
                onConfigRowsChange={setConfigRows}
                initialConfigRows={configRows}
                editMode={false}
                filterId={filterId}
                title={title}
                onTitleChange={setTitle}
              />
            )}
            <div className='esg-analytics-content-area pt-0 2'>
              {stackedBarChartCarouselItems.length > 0 ? (
                <Carousel
                  activeIndex={chartsCarouselIndex}
                  onSelect={(index) => setChartsCarouselIndex(index)}
                  className="esg-analytics-charts-carousel 1"
                  prevIcon={<BiChevronLeft size={28} aria-hidden />}
                  nextIcon={<BiChevronRight size={28} aria-hidden />}
                  prevLabel={t('ESGAnalytics.previousCharts.label') || 'Previous charts'}
                  nextLabel={t('ESGAnalytics.nextCharts.label') || 'Next charts'}
                  indicators={stackedBarChartCarouselItems.length > 1}
                  controls={stackedBarChartCarouselItems.length > 1}
                  interval={null}
                >
                  {stackedBarChartCarouselItems.map((item) => {
                    const carouselConfigRow = getConfigRowForStackedBarCarouselItem(
                      item,
                      configRows
                    );
                    return (
                      <Carousel.Item
                        key={item.itemKind === 'equation' ? `eq-${item.equationId}` : `m-${item.metricId}`}
                        className="esg-analytics-charts-carousel-slide"
                      >
                        <ESGAnalyticsStackedBarChart
                          t={t}
                          dataViewData={dataViewData}
                          configRows={[carouselConfigRow]}
                          allConfigRows={configRows}
                          filter={appliedFilter ?? filter}
                          branches={branches}
                          yearData={yearData}
                          showChartMenu={true}
                          chartTitle={carouselConfigRow?.name}
                        />
                      </Carousel.Item>
                    );
                  })}
                </Carousel>
              ) : (
                <ESGAnalyticsStackedBarChart
                  t={t}
                  dataViewData={dataViewData}
                  configRows={configRows}
                  allConfigRows={configRows}
                  filter={appliedFilter ?? filter}
                  branches={branches}
                  yearData={yearData}
                  showChartMenu={true}
                />
              )}
              <ESGAnalyticsCards
                t={t}
                configRows={configRows}
                dataViewData={dataViewData}
                filter={appliedFilter ?? filter}
                branches={branches}
                dataPoints={dataPoints}
                yearData={yearData}
                section="second"
                equationPopoverClassName="equation-components-popover--wide"
              />
              {/* <ESGAnalyticsLineGraph
                t={t}
                graphData={graphData}
                configRows={configRows}
                showChartMenu={true}
                showMaximizeInMenu={false}
                showViewTableInMenu={false}
              /> */}
              <ESGAnalyticsLineGraphYearly
                t={t}
                dataViewData={dataViewData}
                configRows={configRows}
                filter={appliedFilter ?? filter}
                showChartMenu={true}
                showMaximizeInMenu={false}
                showViewTableInMenu={false}
              />
              <ESGAnalyticsCards
                t={t}
                configRows={configRows}
                dataViewData={dataViewData}
                filter={appliedFilter ?? filter}
                branches={branches}
                dataPoints={dataPoints}
                yearData={yearData}
                section="first"
                equationPopoverClassName="equation-components-popover--wide"
              />
            </div>
          </div>
        </div>
      )}

      {/* Configuration Selection Sidebar Panel */}
      {!hasSummaryData && (
        <div className={`esg-analytics-config-sidebar ${showConfigPanel ? 'esg-analytics-config-sidebar--visible' : ''}`}>
          {showConfigPanel && (
            <ESGAnalyticsConfigPanel
              t={t}
              configList={configList}
              onClose={handleCloseConfigPanel}
              onSelectConfig={handleConfigSelect}
              loading={loadingConfigs}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default withTranslation()(ESGAnalyticsView);
